import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Switch,
    Select,
    message,
    Tooltip,
    Tabs,
    Row,
    Col,
    Divider,
    Alert,
    Typography,
    Dropdown,
    InputNumber
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    GlobalOutlined,
    SafetyCertificateOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    StopOutlined,
    SwapOutlined,
    LinkOutlined,
    CopyOutlined,
    EditOutlined,
    MoreOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import AdminTransferAction from '../../components/AdminTransferAction';
import cdnService from '../../services/cdnService';
import CdnDomainStatusTag from '../../../shared/components/CdnDomainStatusTag';

import { adminPath } from '../../../shared/config/paths';
import AdminSubscriptionEditModal from '../../components/billing/AdminSubscriptionEditModal';


const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

const Domains = () => {
    const navigate = useNavigate();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [hostnames, setHostnames] = useState([]);
    const [cnameModal, setCnameModal] = useState({ visible: false, domain: null });
    const [editModal, setEditModal] = useState({ visible: false, record: null });
    const [subEditId, setSubEditId] = useState(null);
    const [editForm] = Form.useForm();
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [lifecycleModal, setLifecycleModal] = useState({ visible: false, type: '', record: null });
    const [lifecycleForm] = Form.useForm();
    const [lifecycleLoading, setLifecycleLoading] = useState(false);

    useEffect(() => {
        loadDomains();
    }, []);

    const loadDomains = async () => {
        setLoading(true);
        try {
            const response = await cdnService.getDomains();
            if (response.success) {
                setDomains(response.data || []);
            } else {
                message.error(response.error || '加载域名失败');
            }
        } catch (error) {
            message.error('加载域名失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setHostnames([{
            hostname: '',
            origin: '',
            protocol: 'https',
            port: '443',
            mode: 'roundrobin',
            http_version: '1.1',
            websocket_enabled: false,
            proxy_timeout: 60
        }]);
        form.resetFields();
        setModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个域名吗？此操作不可恢复，将同时删除所有关联的源站、规则和证书。',
            okText: '确定删除',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteDomain(id);
                    if (response.success) {
                        message.success('删除成功');
                        loadDomains();
                    } else {
                        message.error(response.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败: ' + error.message);
                }
            }
        });
    };

    const handleShowCname = (record) => {
        setCnameModal({ visible: true, domain: record });
    };

    const handleEditStatus = (record) => {
        setEditModal({ visible: true, record });
        editForm.setFieldsValue({ status: record.status || 'pending' });
    };

    const handleEditStatusSubmit = async () => {
        let values;
        try {
            values = await editForm.validateFields();
        } catch (e) {
            message.error(e?.errorFields?.[0]?.errors?.[0] || '请检查表单');
            return;
        }
        setEditSubmitting(true);
        try {
            const payload = { id: editModal.record.id };
            // 暂停/终止只能走生命周期操作；这里只改 active/pending/inactive
            if (values.status && values.status !== editModal.record.status) {
                payload.status = values.status;
            }
            const res = await cdnService.updateDomain(payload);
            if (res.success) {
                message.success('已保存');
                setEditModal({ visible: false, record: null });
                loadDomains();
            } else {
                message.error(res.error || '更新失败');
            }
        } catch (error) {
            message.error('更新失败: ' + (error.error || error.message || ''));
        } finally {
            setEditSubmitting(false);
        }
    };

    const openLifecycle = (type, record) => {
        lifecycleForm.resetFields();
        setLifecycleModal({ visible: true, type, record });
    };

    const getLifecycleItems = (record) => {
        const status = record.status;
        const items = [];
        if (status !== 'active') {
            items.push({ key: 'activate', label: '激活', icon: <CheckCircleOutlined /> });
        }
        if (status === 'active') {
            items.push({ key: 'suspend', label: '暂停', icon: <PauseCircleOutlined /> });
        }
        if (status === 'suspended') {
            items.push({ key: 'resume', label: '恢复', icon: <PlayCircleOutlined /> });
        }
        if (record.verified === false) {
            items.push({ key: 'mark_verified', label: '标记已验证', icon: <CheckCircleOutlined /> });
        }
        items.push({ key: 'terminate', label: '终止', icon: <StopOutlined />, danger: true });
        return items;
    };

    // 归属验证:管理员直接标记(下发边缘、可签证书)
    const markVerified = (record) => Modal.confirm({
        title: `标记 ${record.domain} 归属已验证?`,
        onOk: async () => {
            const res = await cdnService.markDomainVerified(record.id);
            if (res.success) {
                message.success('已标记');
                loadDomains();
            } else {
                message.error(res.error || '操作失败');
            }
        },
    });

    const lifecycleTitle = () => {
        const titles = {
            suspend: '暂停域名',
            resume: '恢复域名',
            activate: '激活域名',
            terminate: '终止域名',
            change_owner: '转移归属'
        };
        return titles[lifecycleModal.type] || '操作';
    };

    const handleLifecycleSubmit = async () => {
        const { type, record } = lifecycleModal;
        let values = {};
        try {
            values = await lifecycleForm.validateFields();
        } catch {
            return;
        }
        setLifecycleLoading(true);
        try {
            let res;
            switch (type) {
                case 'suspend':
                    res = await cdnService.suspendDomain(record.id, (values.reason || '').trim());
                    break;
                case 'resume':
                    res = await cdnService.resumeDomain(record.id);
                    break;
                case 'activate':
                    res = await cdnService.activateDomain(record.id);
                    break;
                case 'terminate':
                    res = await cdnService.terminateDomain(record.id, values.hard_delete || false);
                    break;
                case 'change_owner':
                    res = await cdnService.changeDomainOwner(record.id, values.new_user_id);
                    break;
                default:
                    throw new Error('未知操作');
            }
            if (res.success) {
                message.success({ suspend: '已暂停', resume: '已恢复', activate: '已激活', terminate: '已终止', change_owner: '已转移归属' }[type] || '操作成功');
                setLifecycleModal({ visible: false, type: '', record: null });
                loadDomains();
            } else {
                message.error(res.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败: ' + (error.error || error.message || ''));
        } finally {
            setLifecycleLoading(false);
        }
    };

    const renderLifecycleContent = () => {
        const { type, record } = lifecycleModal;
        switch (type) {
            case 'suspend':
                return (
                    <Form form={lifecycleForm} layout="vertical">
                        <p>暂停后 {record?.domain} 将从全部边缘节点下线。</p>
                        <Form.Item name="reason" label="暂停原因" rules={[{ required: true, whitespace: true, message: '请填写暂停原因' }]}>
                            <Input.TextArea rows={3} maxLength={200} placeholder="如：欠费、滥用投诉" />
                        </Form.Item>
                    </Form>
                );
            case 'terminate':
                return (
                    <Form form={lifecycleForm} layout="vertical">
                        <Alert message="警告" description="终止域名将停止其 CDN 加速服务，并触发节点下线与订阅联动。" type="warning" showIcon style={{ marginBottom: 16 }} />
                        <p>确定要终止域名 {record?.domain} 吗？</p>
                        <Form.Item name="hard_delete" valuePropName="checked" initialValue={false}>
                            <Switch checkedChildren="永久删除" unCheckedChildren="保留数据" />
                        </Form.Item>
                    </Form>
                );
            case 'change_owner':
                return (
                    <Form form={lifecycleForm} layout="vertical">
                        <p>将域名 {record?.domain} 转移给其他用户</p>
                        <Form.Item name="new_user_id" label="新用户ID" rules={[{ required: true, message: '请输入新用户ID' }]}>
                            <InputNumber style={{ width: '100%' }} min={1} placeholder="请输入新用户ID" />
                        </Form.Item>
                    </Form>
                );
            case 'resume':
            case 'activate':
                return <p>确定要{type === 'resume' ? '恢复' : '激活'}域名 {record?.domain} 吗？</p>;
            default:
                return null;
        }
    };

    const copyCname = (value) => {
        if (!value) return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(value)
                .then(() => message.success('已复制到剪贴板'))
                .catch(() => message.error('复制失败'));
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                message.success('已复制到剪贴板');
            } catch (err) {
                message.error('复制失败');
            }
            document.body.removeChild(textArea);
        }
    };

    const handleSubmit = async () => {
        if (addSubmitting) return; // 防重复提交
        try {
            const values = await form.validateFields();

            const preparedHostnames = hostnames
                .filter(h => (h.origin || '').trim())
                .map(h => ({
                    hostname: (h.hostname && h.hostname.trim()) || values.domain,
                    origin: h.origin.trim(),
                    protocol: h.protocol || 'https',
                    port: h.port || '443',
                    mode: h.mode || 'roundrobin',
                    http_version: h.http_version || '1.1',
                    websocket_enabled: h.websocket_enabled ? true : null, // 未开 = 跟随域名
                    proxy_timeout: h.proxy_timeout || 60
                }));

            if (preparedHostnames.length === 0) {
                message.error('至少需要配置一个有效源站地址');
                return;
            }

            setAddSubmitting(true);

            const data = {
                ...values,
                ssl_enabled: !!values.ssl_enabled,
                waf_enabled: !!values.waf_enabled,
                hostnames: preparedHostnames
            };

            const response = await cdnService.createDomain(data);

            if (response.success) {
                message.success('创建成功');
                setModalVisible(false);
                loadDomains();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请填写所有必填项');
            } else {
                message.error('操作失败: ' + error.message);
            }
        } finally {
            setAddSubmitting(false);
        }
    };

    const addHostname = () => {
        setHostnames([...hostnames, {
            hostname: '',
            origin: '',
            protocol: 'https',
            port: '443',
            mode: 'roundrobin',
            http_version: '1.1',
            websocket_enabled: false,
            proxy_timeout: 60
        }]);
    };

    const removeHostname = (index) => {
        if (hostnames.length > 1) {
            setHostnames(hostnames.filter((_, i) => i !== index));
        } else {
            message.warning('至少需要保留一个主机配置');
        }
    };

    const updateHostname = (index, field, value) => {
        const newHostnames = [...hostnames];
        newHostnames[index][field] = value;
        setHostnames(newHostnames);
    };

    const columns = [
        {
            title: '域名',
            dataIndex: 'domain',
            key: 'domain',
            render: (text) => (
                <Space>
                    <GlobalOutlined />
                    <strong>{text}</strong>
                </Space>
            )
        },
        {
            title: '用户',
            dataIndex: 'user_email',
            key: 'user_email',
            ellipsis: true,
            render: (email, r) => email || (r.user_id ? `#${r.user_id}` : '-')
        },
        {
            title: '源站',
            dataIndex: 'hostname_count',
            key: 'hostname_count',
            render: (count) => (
                <span>{count || 0} 个源站</span>
            )
        },
        {
            title: 'SSL',
            dataIndex: 'ssl_enabled',
            key: 'ssl_enabled',
            render: (enabled) => !!enabled ? (
                <Tag icon={<SafetyCertificateOutlined />} color="success">已启用</Tag>
            ) : (
                <Tag color="default">未启用</Tag>
            )
        },
        {
            title: 'WAF',
            dataIndex: 'waf_enabled',
            key: 'waf_enabled',
            render: (enabled) => !!enabled ? (
                <Tag icon={<ThunderboltOutlined />} color="processing">已启用</Tag>
            ) : (
                <Tag color="default">未启用</Tag>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status, r) => (
                <Space size={4}>
                    <CdnDomainStatusTag status={status} />
                    {r.verified === false && (
                        <Tooltip title={r.verify_deadline ? `${String(r.verify_deadline).slice(0, 10)} 前未验证将移除` : ''}><Tag color="orange">未验证</Tag></Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: '套餐 / 到期',
            key: 'plan',
            render: (_, r) => (r.subscription ? (
                <Button type="link" size="small" style={{ padding: 0, height: 'auto', textAlign: 'left' }} onClick={() => setSubEditId(r.subscription.id)}>
                    <Space direction="vertical" size={0}>
                        <span>{r.subscription.product_name || `#${r.subscription.product_id}`}</span>
                        <span style={{ color: '#999', fontSize: 12 }}>{r.subscription.expiry_date ? String(r.subscription.expiry_date).slice(0, 10) : '不过期'}</span>
                    </Space>
                </Button>
            ) : <span style={{ color: '#999' }}>无套餐</span>)
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-'
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <AdminTransferAction type="cdn" id={record.id} name={record.domain} onDone={() => loadDomains()}>
                        <Tooltip title="转移给其他账号"><Button type="link" size="small" icon={<SwapOutlined />} /></Tooltip>
                    </AdminTransferAction>
                    <Tooltip title="查看详情">
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(adminPath(`/cdn/domains/${record.id}`))}
                        />
                    </Tooltip>
                    <Tooltip title="查看 CNAME">
                        <Button
                            type="link"
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() => handleShowCname(record)}
                        />
                    </Tooltip>
                    {record.subscription && (
                        <Tooltip title="套餐 / 到期">
                            <Button type="link" size="small" icon={<CalendarOutlined />} onClick={() => setSubEditId(record.subscription.id)} />
                        </Tooltip>
                    )}
                    <Tooltip title="激活状态">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditStatus(record)}
                        />
                    </Tooltip>
                    <Dropdown
                        menu={{
                            items: getLifecycleItems(record),
                            onClick: ({ key }) => (key === 'mark_verified' ? markVerified(record) : openLifecycle(key, record))
                        }}
                        trigger={['click']}
                    >
                        <Tooltip title="生命周期操作">
                            <Button type="link" size="small" icon={<MoreOutlined />} />
                        </Tooltip>
                    </Dropdown>
                    <Tooltip title="删除">
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const cnameData = cnameModal.domain?.hostnames || [];

    return (
        <div>
            <Card
                title="CDN域名管理"
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadDomains}
                            loading={loading}
                        >
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            添加域名
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={domains}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 个域名`
                    }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="暂无域名" /> }}
                />
            </Card>

            {/* 添加域名 Modal */}
            <Modal
                title="添加域名"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
                confirmLoading={addSubmitting}
                width={800}
                okText="确定"
                cancelText="取消"
            >
                <Tabs defaultActiveKey="basic">
                    <TabPane tab="基本信息" key="basic">
                        <Form form={form} layout="vertical">
                            <Form.Item
                                label="域名"
                                name="domain"
                                rules={[{ required: true, message: '请输入域名' }]}
                            >
                                <Input placeholder="example.com" />
                            </Form.Item>

                            <Alert
                                message="域名配置"
                                description="域名基础设置。源站配置请使用【源站设置】标签页。"
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="启用SSL"
                                        name="ssl_enabled"
                                        valuePropName="checked"
                                        initialValue={true}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="启用WAF"
                                        name="waf_enabled"
                                        valuePropName="checked"
                                        initialValue={false}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider orientation="left">高级代理设置</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        label="HTTP版本"
                                        name="http_version"
                                        initialValue="1.1"
                                        tooltip="回源请求使用的HTTP协议版本"
                                    >
                                        <Select>
                                            <Option value="1.0">HTTP/1.0</Option>
                                            <Option value="1.1">HTTP/1.1</Option>
                                            <Option value="2">HTTP/2</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="WebSocket支持"
                                        name="websocket_enabled"
                                        valuePropName="checked"
                                        initialValue={false}
                                        tooltip="启用后支持WebSocket连接"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="代理超时(秒)"
                                        name="proxy_timeout"
                                        initialValue={60}
                                        tooltip="代理连接超时时间，WebSocket建议设置更长"
                                    >
                                        <Input type="number" min={1} max={3600} placeholder="60" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </TabPane>

                    <TabPane tab="源站设置" key="origins">
                        <Alert
                            message="多源站负载均衡"
                            description="配置多个源站实现负载均衡和高可用。每个主机可以独立配置源站地址、协议和端口。"
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        {hostnames.map((hostname, index) => (
                            <Card
                                key={index}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={`主机配置 ${index + 1}`}
                                extra={
                                    hostnames.length > 1 && (
                                        <Button
                                            type="link"
                                            danger
                                            size="small"
                                            onClick={() => removeHostname(index)}
                                        >
                                            删除
                                        </Button>
                                    )
                                }
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="主机名">
                                            <Input
                                                value={hostname.hostname}
                                                onChange={(e) => updateHostname(index, 'hostname', e.target.value)}
                                                placeholder="www.example.com"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="源站地址">
                                            <Input
                                                value={hostname.origin}
                                                onChange={(e) => updateHostname(index, 'origin', e.target.value)}
                                                placeholder="origin.example.com"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item label="协议">
                                            <Select
                                                value={hostname.protocol}
                                                onChange={(value) => updateHostname(index, 'protocol', value)}
                                            >
                                                <Option value="http">HTTP</Option>
                                                <Option value="https">HTTPS</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="端口">
                                            <Input
                                                value={hostname.port}
                                                onChange={(e) => updateHostname(index, 'port', e.target.value)}
                                                placeholder="443"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="负载模式">
                                            <Select
                                                value={hostname.mode}
                                                onChange={(value) => updateHostname(index, 'mode', value)}
                                            >
                                                <Option value="roundrobin">轮询</Option>
                                                <Option value="iphash">IP Hash</Option>
                                                <Option value="leastconn">最少连接</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item label="HTTP版本">
                                            <Select
                                                value={hostname.http_version || '1.1'}
                                                onChange={(value) => updateHostname(index, 'http_version', value)}
                                            >
                                                <Option value="1.0">HTTP/1.0</Option>
                                                <Option value="1.1">HTTP/1.1</Option>
                                                <Option value="2">HTTP/2</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="WebSocket">
                                            <Switch
                                                checked={hostname.websocket_enabled || false}
                                                onChange={(checked) => updateHostname(index, 'websocket_enabled', checked)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="超时(秒)">
                                            <Input
                                                type="number"
                                                value={hostname.proxy_timeout || 60}
                                                onChange={(e) => updateHostname(index, 'proxy_timeout', parseInt(e.target.value) || 60)}
                                                placeholder="60"
                                                min={1}
                                                max={3600}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}

                        <Button
                            type="dashed"
                            onClick={addHostname}
                            block
                            icon={<PlusOutlined />}
                        >
                            添加主机配置
                        </Button>
                    </TabPane>
                </Tabs>
            </Modal>

            {/* 查看 CNAME Modal */}
            <Modal
                title={`CNAME 记录 - ${cnameModal.domain?.domain || ''}`}
                open={cnameModal.visible}
                onCancel={() => setCnameModal({ visible: false, domain: null })}
                footer={[
                    <Button key="close" onClick={() => setCnameModal({ visible: false, domain: null })}>
                        关闭
                    </Button>
                ]}
                width={700}
            >
                {cnameData.length > 0 ? (
                    <div>
                        <Alert
                            message="请将以下主机名的 CNAME 记录指向对应的值"
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        {cnameData.map((h, idx) => (
                            <Card key={idx} size="small" style={{ marginBottom: 8 }}>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>{h.hostname}</Text>
                                </div>
                                {h.cname_target ? (
                                    <Space>
                                        <Text code style={{ fontSize: 12 }}>{h.cname_target}</Text>
                                        <Tooltip title="复制">
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={() => copyCname(h.cname_target)}
                                            />
                                        </Tooltip>
                                    </Space>
                                ) : (
                                    <Text type="secondary">CNAME 未生成</Text>
                                )}
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Alert
                        message="该域名暂无配置的源站主机名"
                        description="请先在域名详情页添加源站配置"
                        type="info"
                        showIcon
                    />
                )}
            </Modal>

            {/* 编辑激活状态 / 过期时间 Modal */}
            <Modal
                title={`激活状态 - ${editModal.record?.domain || ''}`}
                open={editModal.visible}
                onCancel={() => setEditModal({ visible: false, record: null })}
                onOk={handleEditStatusSubmit}
                confirmLoading={editSubmitting}
                okText="保存"
                cancelText="取消"
                destroyOnClose
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item
                        name="status"
                        label="激活状态"
                        rules={[{ required: true, message: '请选择状态' }]}
                        extra={['suspended', 'terminated'].includes(editModal.record?.status)
                            ? (editModal.record?.status === 'suspended' ? '已暂停，请用「恢复」操作' : '已终止，请用「激活」操作')
                            : undefined}
                    >
                        <Select disabled={['suspended', 'terminated'].includes(editModal.record?.status)}>
                            <Option value="active">生效中 (active)</Option>
                            <Option value="pending">待处理 (pending)</Option>
                            <Option value="inactive">未激活 (inactive)</Option>
                            {editModal.record?.status === 'suspended' && <Option value="suspended" disabled>已暂停 (suspended)</Option>}
                            {editModal.record?.status === 'terminated' && <Option value="terminated" disabled>已终止 (terminated)</Option>}
                        </Select>
                    </Form.Item>
                    {editModal.record && editModal.record.status !== 'terminated' && (
                        <Form.Item label="服务">
                            {editModal.record.status === 'suspended' ? (
                                <Button icon={<PlayCircleOutlined />} onClick={() => { const r = editModal.record; setEditModal({ visible: false, record: null }); openLifecycle('resume', r); }}>恢复</Button>
                            ) : (
                                <Button danger icon={<PauseCircleOutlined />} onClick={() => { const r = editModal.record; setEditModal({ visible: false, record: null }); openLifecycle('suspend', r); }}>暂停</Button>
                            )}
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <AdminSubscriptionEditModal open={!!subEditId} subscriptionId={subEditId} onClose={() => setSubEditId(null)} onSaved={() => loadDomains()} />

            {/* 生命周期操作 Modal */}
            <Modal
                title={`${lifecycleTitle()} - ${lifecycleModal.record?.domain || ''}`}
                open={lifecycleModal.visible}
                onCancel={() => setLifecycleModal({ visible: false, type: '', record: null })}
                onOk={handleLifecycleSubmit}
                confirmLoading={lifecycleLoading}
                okText="确定"
                cancelText="取消"
                okButtonProps={{ danger: ['terminate', 'suspend'].includes(lifecycleModal.type) }}
                destroyOnClose
            >
                {renderLifecycleContent()}
            </Modal>
        </div>
    );
};

export default Domains;
